<script setup lang="ts">
import { computed } from 'vue';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/vue-3';
import { useApolloClient } from '@vue/apollo-composable';

import { openLightboxFromImage } from '../../UImageLightbox/lightbox-store';
import { clearUpload, getUploadState } from './upload-registry';
import { refreshBinaryFileUrls, resolveBinaryFileUrls } from './useBinaryFileHydration';

const props = defineProps<NodeViewProps>();

const { client } = useApolloClient();

const fileId = computed(() => props.node.attrs.fileId as string | null);
const uploadId = computed(() => props.node.attrs.uploadId as string | null);

const uploadState = computed(() => getUploadState(uploadId.value));
const hydrated = computed(() => (fileId.value ? resolveBinaryFileUrls(client, fileId.value) : null));

const isUploading = computed(() => !fileId.value && uploadState.value?.status === 'uploading');
const uploadProgress = computed(() => uploadState.value?.progress ?? 0);

/**
 * Display source priority: local object-URL preview while uploading → hydrated
 * presigned URL for managed files → raw src for external (non-managed) images.
 */
const displaySrc = computed(() => {
  if (uploadState.value?.previewUrl) {
    return uploadState.value.previewUrl;
  }

  if (fileId.value) {
    return hydrated.value?.status === 'ready' ? hydrated.value.url ?? null : null;
  }

  const src = props.node.attrs.src as string | null;

  // Sentinel src without hydration means the fileId attr was lost — treat as broken.
  return src && !src.startsWith('st-file://') ? src : null;
});

const isResolving = computed(() => Boolean(fileId.value) && hydrated.value?.status === 'pending');

const isError = computed(() => {
  if (isUploading.value || isResolving.value) {
    return false;
  }

  if (uploadState.value?.status === 'error') {
    return true;
  }

  if (fileId.value) {
    return hydrated.value?.status === 'error';
  }

  return displaySrc.value === null;
});

const errorMessage = computed(() => {
  if (uploadState.value?.errorMessage) {
    return uploadState.value.errorMessage;
  }

  return 'Failed to load the image.';
});

/** A presigned URL may expire while the document is open — refetch once. */
const onImageError = () => {
  if (fileId.value && !uploadState.value?.previewUrl) {
    refreshBinaryFileUrls(client, fileId.value);
  }
};

const onRemove = () => {
  if (uploadId.value) {
    clearUpload(uploadId.value);
  }

  props.deleteNode();
};

/**
 * A local upload preview has no stable URL yet and its node is about to be
 * replaced — only settled images take part in the lightbox.
 */
const isViewable = computed(() => Boolean(displaySrc.value) && !isUploading.value);

const openPreview = (event: MouseEvent) => {
  if (!isViewable.value) {
    return;
  }

  openLightboxFromImage(event.currentTarget as HTMLImageElement);
};

/**
 * Linear's split: in a read-only document a single click opens the image,
 * while in the editor a click has to stay available for selecting the node, so
 * the preview moves to a double-click.
 */
const onImageClick = (event: MouseEvent) => {
  if (props.editor.isEditable) {
    return;
  }

  openPreview(event);
};

const onImageDoubleClick = (event: MouseEvent) => {
  if (!props.editor.isEditable) {
    return;
  }

  openPreview(event);
};

const downloadUrl = computed(() =>
  (fileId.value && hydrated.value?.status === 'ready' ? hydrated.value.downloadUrl ?? null : null));
</script>

<template>
  <NodeViewWrapper
    class="my-2"
    data-testid="editor-attachment-image"
  >
    <!-- Error card -->
    <div
      v-if="isError"
      class="flex items-center gap-3 rounded-[10px] border border-error/40 bg-error/10 p-3"
    >
      <UIcon
        name="i-lucide-image-off"
        class="size-5 text-error shrink-0"
      />
      <p class="text-sm text-error min-w-0 truncate">
        {{ errorMessage }}
      </p>
      <button
        v-if="props.editor.isEditable"
        type="button"
        class="ml-auto shrink-0 text-error hover:text-error/80 transition-colors p-1"
        title="Remove"
        @click="onRemove"
      >
        <UIcon
          name="i-lucide-x"
          class="size-4"
        />
      </button>
    </div>

    <!-- Resolving skeleton -->
    <div
      v-else-if="!displaySrc"
      class="h-40 max-w-md animate-pulse rounded-lg bg-accented"
    />

    <!-- Image (uploading preview or resolved) -->
    <div
      v-else
      class="relative inline-block max-w-full"
    >
      <img
        data-testid="editor-attachment-image-visual"
        :src="displaySrc"
        :alt="(props.node.attrs.alt as string) || ''"
        class="max-w-full max-h-[32rem] rounded-lg"
        :class="[
          isUploading ? 'opacity-60' : '',
          props.selected && props.editor.isEditable ? 'ring-2 ring-primary' : '',
          isViewable ? 'cursor-zoom-in' : '',
        ]"
        :data-lightbox="isViewable ? '' : undefined"
        :data-lightbox-download="downloadUrl ?? undefined"
        :data-lightbox-name="(props.node.attrs.alt as string) || undefined"
        :title="isViewable ? (props.editor.isEditable ? 'Double-click to open' : 'Click to open') : undefined"
        draggable="false"
        @error="onImageError"
        @click="onImageClick"
        @dblclick="onImageDoubleClick"
      >

      <!-- Upload progress overlay -->
      <div
        v-if="isUploading"
        class="absolute inset-x-3 bottom-3 h-1.5 overflow-hidden rounded-full bg-default/60"
      >
        <div
          class="h-full rounded-full bg-primary transition-[width]"
          :style="{ width: `${uploadProgress}%` }"
        />
      </div>
    </div>
  </NodeViewWrapper>
</template>
